import { createColumnHelper } from "@tanstack/react-table";
import { basicDate } from "helpers/time";
import { InternalLink } from "components/Link";
import CDS, { ActionTypeTag } from "design-system";
import ControlledDataTable, { ControlledDataTableProps } from "components/Table/ControlledDataTable";
import { IssueId } from "models/issue";
import { RandomEmptyIssueActionsState } from "components/EmptyComponents/RandomEmptyState";
import { Flex, Tooltip } from "@chakra-ui/react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ShowIssuesOrReadyByIdTag } from "features/Issues/Components/NumberOfIssuesTag";
import ActionRowResolutionButtons from "features/Issues/Components/ActionRowResolutionButtons";
import { ActionSummary } from "features/Resources/Shared/models";
import { primaryTriggerDate, isActionResolved } from "features/Resources/Shared/helpers";
import { DisplayRecipientsColumn } from "features/Resources/Shared/Components/DisplayRecipients";
import { actionPathByPrefixId } from "helpers/url/pages/v2/actionPaths";
import { useSharedResourcesListByIssueIdQuery } from "features/Resources/Shared/hooks";

const columnHelper = createColumnHelper<ActionSummary>();

const createColumnDefs = (issueId: string) => [
  columnHelper.accessor((row) => row, {
    id: "actionType",
    header: "Type",
    cell: (row) => {
      const summary = row.getValue();
      const meetingType = summary?.actionType === "meeting" ? (summary).action?.meetingType : undefined;
      return <ActionTypeTag.IconBadgeWithMeetingType actionType={summary?.actionType} meetingType={meetingType} />;
    },
    maxSize: 32,
    minSize: 32,
    size: 32,
    meta: {
      sx: {
        paddingLeft: 6,
        paddingRight: 4,
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    cell: (row) => (
      <InternalLink
        to={actionPathByPrefixId(row.getValue()?.action?.prefixId)}
        textDecoration={isActionResolved(row.getValue()?.action) ? "line-through" : "none"}
        newTab
      >
        {row.getValue()?.action?.name}
      </InternalLink>
    ),
    maxSize: 300,
    minSize: 200,
    size: 300,
    meta: {
      sx: {
        paddingLeft: 0,
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "sendDate",
    header: "Send Date",
    cell: (row) => {
      const date = primaryTriggerDate(row.getValue());
      if (!date) return null;
      const parsed = typeof date === "string" ? parseISO(date) : new Date(date);
      return (
        <Tooltip label={basicDate(date)} placement="top" hasArrow>
          <Flex direction="column" gap="0">
            <CDS.Text12>{basicDate(date)}</CDS.Text12>
            <CDS.Text10 color="content.tertiary">
              {formatDistanceToNow(parsed, { addSuffix: true })}
            </CDS.Text10>
          </Flex>
        </Tooltip>
      );
    },
    minSize: 119,
    maxSize: 119,
    sortingFn: (rowA, rowB) => {
      const dateA = primaryTriggerDate(rowA.original) ? new Date(primaryTriggerDate(rowA.original)) : null;
      const dateB = primaryTriggerDate(rowB.original) ? new Date(primaryTriggerDate(rowB.original)) : null;

      if (dateA === null && dateB === null) return 0;
      if (dateA === null) return -1;
      if (dateB === null) return 1;

      return dateA - dateB;
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "target",
    header: "Recipient",
    cell: (row) => <DisplayRecipientsColumn action={row.getValue()} />,
    minSize: 140,
    maxSize: 140,
  }),
  columnHelper.accessor((row) => row, {
    id: "issuesCount",
    header: "",
    cell: (row) => (
      <Flex width="fit-content" justify="flex-start">
        <ShowIssuesOrReadyByIdTag
          issuesCount={row.getValue()?.action?.issueIds?.length || 0}
          action={row.getValue()?.action}
        />
      </Flex>
    ),
    minSize: 100,
    maxSize: 100,
    meta: { headerHStackProps: { justifyContent: "center" } },
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row, {
    id: "moreOptions",
    header: "",
    cell: (row) => <ActionRowResolutionButtons action={row.getValue()?.action} issueId={issueId} />,
  }),
];

interface IndividualIssueActionTableProps {
  issueId: IssueId;
  tableDefaultSort?: { id: string; desc: boolean };
}

export default function IndividualIssueActionTable({
  issueId,
  tableDefaultSort = { id: "sendDate", desc: false },
}: IndividualIssueActionTableProps) {
  const { data, isLoading } = useSharedResourcesListByIssueIdQuery(issueId);

  const tableProps: ControlledDataTableProps<ActionSummary> = {
    data,
    columns: createColumnDefs(issueId),
    initialPagination: { pageSize: 20, pageIndex: 0 },
    defaultSort: [tableDefaultSort],
    textWrap: true,
    emptyStateComponent: !issueId && !isLoading ? <RandomEmptyIssueActionsState /> : null,
    isLoading,
  };

  return <ControlledDataTable sx={{ width: "100%" }} {...tableProps} />;
}
